import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { API_BASE } from "../lib/api";

const TRACKING_DISABLED_PATHS = [/^\/admin\b/i, /^\/dealer\b/i, /^\/client\b/i, /^\/login\b/i, /^\/play\b/i, /^\/preview\b/i];

function id(prefix) {
  const cryptoId = typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  return `${prefix}-${cryptoId}`;
}

function storageId(store, key, prefix) {
  try {
    let value = store.getItem(key);
    if (!value) {
      value = id(prefix);
      store.setItem(key, value);
    }
    return value;
  } catch {
    return id(prefix);
  }
}

function deviceType() {
  const ua = navigator.userAgent || "";
  if (/ipad|tablet/i.test(ua)) return "tablet";
  if (/mobile|android|iphone/i.test(ua)) return "mobile";
  return "desktop";
}

function sourceFromReferrer() {
  const ref = document.referrer || "";
  const lower = ref.toLowerCase();
  if (!ref) return "direct";
  if (lower.includes("google.")) return "google";
  if (lower.includes("facebook.") || lower.includes("fb.")) return "facebook";
  if (lower.includes("instagram.")) return "instagram";
  if (lower.includes("whatsapp") || lower.includes("wa.me")) return "whatsapp";
  return "referral";
}

function send(path, payload, keepalive = false) {
  const url = `${API_BASE}${path}`;
  const body = JSON.stringify(payload);
  if (keepalive && navigator.sendBeacon) {
    const blob = new Blob([body], { type: "application/json" });
    navigator.sendBeacon(url, blob);
    return;
  }
  fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body,
    keepalive,
    credentials: "include",
  }).catch(() => {});
}

function basePayload(visitorId, sessionId) {
  return {
    visitorId,
    sessionId,
    userAgent: navigator.userAgent || "",
    deviceType: deviceType(),
    referrer: document.referrer || "",
    source: sourceFromReferrer(),
  };
}

function fileNameFromUrl(url) {
  try {
    const parsed = new URL(url, window.location.origin);
    return decodeURIComponent(parsed.pathname.split("/").filter(Boolean).pop() || "Download");
  } catch {
    return "Download";
  }
}

function eventLabelForLink(link) {
  return link.getAttribute("aria-label") || link.getAttribute("title") || link.textContent?.trim() || link.href || "";
}

export default function WebsiteAnalyticsTracker() {
  const location = useLocation();
  const activeRef = useRef(null);
  const visitorId = storageId(window.localStorage, "rp_analytics_visitor_id", "visitor");
  const sessionId = storageId(window.sessionStorage, "rp_analytics_session_id", "session");
  const disabled = TRACKING_DISABLED_PATHS.some((pattern) => pattern.test(location.pathname));

  useEffect(() => {
    if (disabled) return undefined;
    const startedAt = Date.now();
    const pageViewId = id("pv");
    const pageUrl = `${location.pathname}${location.search}${location.hash}`;
    activeRef.current = { pageViewId, pageUrl, startedAt };
    const timer = window.setTimeout(() => {
      send("/analytics/track-page-view", {
        ...basePayload(visitorId, sessionId),
        pageViewId,
        pageUrl,
        pageTitle: document.title || "",
        durationSeconds: 0,
        visitedAt: new Date(startedAt).toISOString(),
      });
      if (/\/products\/|\/e-catalogue\//i.test(pageUrl)) {
        send("/analytics/track-event", {
          ...basePayload(visitorId, sessionId),
          eventType: "product_detail_view",
          eventLabel: document.title || pageUrl,
          pageUrl,
          createdAt: new Date().toISOString(),
        });
      }
      if (/\/contact/i.test(pageUrl)) {
        send("/analytics/track-event", {
          ...basePayload(visitorId, sessionId),
          eventType: "contact_page_visit",
          eventLabel: document.title || pageUrl,
          pageUrl,
          createdAt: new Date().toISOString(),
        });
      }
    }, 600);

    return () => {
      window.clearTimeout(timer);
      const durationSeconds = Math.max(0, Math.round((Date.now() - startedAt) / 1000));
      send("/analytics/track-page-view", {
        ...basePayload(visitorId, sessionId),
        pageViewId,
        pageUrl,
        pageTitle: document.title || "",
        durationSeconds,
        visitedAt: new Date(startedAt).toISOString(),
      }, true);
    };
  }, [disabled, location.pathname, location.search, location.hash, sessionId, visitorId]);

  useEffect(() => {
    if (disabled) return undefined;
    const onClick = (event) => {
      const link = event.target?.closest?.("a[href]");
      const button = event.target?.closest?.("button");
      const common = basePayload(visitorId, sessionId);
      const pageUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`;
      if (link) {
        const href = link.getAttribute("href") || "";
        const label = eventLabelForLink(link);
        if (/wa\.me|whatsapp/i.test(href)) {
          send("/analytics/track-event", { ...common, eventType: "whatsapp_click", eventLabel: label, pageUrl, createdAt: new Date().toISOString() }, true);
        } else if (/^tel:/i.test(href)) {
          send("/analytics/track-event", { ...common, eventType: "call_click", eventLabel: label, pageUrl, createdAt: new Date().toISOString() }, true);
        }
        if (link.hasAttribute("download") || /\.(pdf|docx?|xlsx?|csv|zip)(\?|#|$)/i.test(href) || /brochure|catalogue|price-list|specification/i.test(href)) {
          send("/analytics/track-download", {
            ...common,
            fileName: link.getAttribute("download") || label || fileNameFromUrl(href),
            fileUrl: href,
            category: /catalogue|e-catalogue/i.test(`${href} ${pageUrl}`) ? "E-catalogue PDF" : "Downloadable file",
            pageUrl,
            downloadedAt: new Date().toISOString(),
          }, true);
        }
      } else if (button) {
        const label = button.getAttribute("aria-label") || button.getAttribute("title") || button.textContent?.trim() || "";
        if (/demo/i.test(label)) {
          send("/analytics/track-event", { ...common, eventType: "demo_request", eventLabel: label, pageUrl, createdAt: new Date().toISOString() }, true);
        }
      }
    };
    document.addEventListener("click", onClick, true);
    return () => {
      document.removeEventListener("click", onClick, true);
    };
  }, [disabled, sessionId, visitorId]);

  useEffect(() => {
    if (disabled) return undefined;
    const onBeforeUnload = () => {
      const active = activeRef.current;
      if (!active) return;
      send("/analytics/track-page-view", {
        ...basePayload(visitorId, sessionId),
        pageViewId: active.pageViewId,
        pageUrl: active.pageUrl,
        pageTitle: document.title || "",
        durationSeconds: Math.max(0, Math.round((Date.now() - active.startedAt) / 1000)),
        visitedAt: new Date(active.startedAt).toISOString(),
      }, true);
    };
    window.addEventListener("pagehide", onBeforeUnload);
    return () => window.removeEventListener("pagehide", onBeforeUnload);
  }, [disabled, sessionId, visitorId]);

  return null;
}
